<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { NavLinkProps } from '@phila/phila-ui-nav-link'
import AccountMenu from '@/components/AccountMenu.vue'
import ModalSpinner from '@/components/modal-spinner.vue'
import { useAuthStore } from '@/stores/auth'
import { useConfirmDialog } from '@/composables/useConfirm'
import { useMessageDialog } from '@/composables/useMessage'
import { useIsMobile } from '@/composables/useIsMobile'
import streetsLogo from '@/assets/images/logo-streets-dot-white.png'

const authStore = useAuthStore()
const route = useRoute()
const isMobile = useIsMobile()

// The app's one confirm dialog and one message dialog. They live here so a
// page can ask a question or report a result without owning the markup.
const {
  visible: confirmVisible,
  title: confirmTitle,
  message: confirmMessage,
  detail: confirmDetail,
  destructive: confirmDestructive,
  accept: acceptConfirm,
  dismiss: dismissConfirm,
} = useConfirmDialog()

const {
  visible: messageVisible,
  title: messageTitle,
  message: messageText,
  detail: messageDetail,
  close: closeMessage,
} = useMessageDialog()

// The template's modal takes one plain-text message; the detail (the entry
// being deleted, the call-out text) goes on the line under it.
function withDetail(message: string, detail: string): string {
  return [message, detail].filter(Boolean).join('\n')
}

const isLoginPage = computed(() => route.name === 'login')

// Help Center is a section rather than a single page: every topic is its own
// flat route, so match the section by path.
const isHelpSection = computed(() => route.path === '/help' || route.path.startsWith('/help/'))

// Nav is only meaningful once signed in; the login screen shows a bare header.
// `active` is what MobileNav uses to light the current section. TabsNav
// forwards only href/text/type, so the desktop tab is lit from the stylesheet
// below instead.
const navLinks = computed<NavLinkProps[]>(() =>
  authStore.isAuthenticated
    ? [
        { href: { name: 'home' }, text: 'Home' },
        { href: { name: 'viewtimeday' }, text: 'Enter Time' },
        { href: { name: 'viewtime' }, text: 'View Time' },
        { href: { name: 'help' }, text: 'Help Center', active: isHelpSection.value },
      ]
    : [],
)

// The Streets DOT logo before the app name, as on the public site; AppHeader
// draws the white rule between them. Sized in the stylesheet below.
const brandingImage = { src: streetsLogo, alt: 'Streets Department of Transportation' }

// The policy pages live in the help center, and their routes are public, so the
// footer works on the login screen as well. The help center itself is reached
// from the tab bar, so the footer carries only the policies, as the template does.
const footerLinks: NavLinkProps[] = [
  { href: { name: 'help-terms-of-use' }, text: 'Terms of Use' },
  { href: { name: 'help-privacy' }, text: 'Privacy Policy' },
  { href: { name: 'help-accessibility' }, text: 'Accessibility' },
]

// The mobile menu carries the account actions the desktop dropdown has, plus
// the policy links, since the footer is hidden on phones.
const mobileLinks = computed<NavLinkProps[]>(() => [
  ...navLinks.value,
  ...(authStore.isAuthenticated
    ? [
        { href: { name: 'profile' }, text: 'Profile' } as NavLinkProps,
        { href: { name: 'about' }, text: 'About' } as NavLinkProps,
        { href: { name: 'logout' }, text: 'Log out' } as NavLinkProps,
      ]
    : []),
  ...footerLinks,
])
</script>

<template>
  <div
    id="app"
    :class="{ 'is-login-page': isLoginPage, 'is-help-section': isHelpSection }"
  >
    <!-- Ahead of everything, and the first thing Tab reaches. Hidden until it
         takes focus, so only the keyboard ever sees it. -->
    <a
      class="skip-link"
      href="#main-content"
    >Skip to main content</a>

    <!-- Keyed on the auth state: AppHeader decides which slots to render from a
         non-reactive read of `slots` on first render, so it has to remount
         once at sign-in for the tab bar, account menu and mobile nav to appear. -->
    <app-header
      :key="`header-${authStore.isAuthenticated}`"
      app-title="XGuard"
      app-subtitle="Crossing Guards"
      app-link="/"
      :branding-image="brandingImage"
    >
      <template #right-nav>
        <AccountMenu />
      </template>
      <template
        v-if="!isMobile && navLinks.length"
        #tabs-nav
      >
        <tabs-nav :links="navLinks" />
      </template>
      <template
        v-if="mobileLinks.length"
        #mobile-nav
      >
        <mobile-nav :links="mobileLinks" />
      </template>
    </app-header>

    <!-- tabindex="-1" so the skip link can actually put focus here. -->
    <main
      id="main-content"
      tabindex="-1"
    >
      <router-view />
    </main>

    <!-- Hidden on phones, where the page's sticky action bar takes the bottom
         of the screen instead; the same links are in the mobile menu. -->
    <app-footer
      :links="footerLinks"
      :is-hidden-mobile="true"
    />

    <!-- Rendered here rather than inside a page so they survive the navigation
         they are asking about. Both answers settle the promise the caller is
         waiting on. -->
    <ModalSpinner
      :model-value="confirmVisible"
      :title="confirmTitle"
      :message="withDetail(confirmMessage, confirmDetail)"
      :is-loading="false"
      :is-proceed="true"
      :is-destructive="confirmDestructive"
      @update:model-value="dismissConfirm"
      @proceed="acceptConfirm"
    />
    <ModalSpinner
      :model-value="messageVisible"
      :title="messageTitle"
      :message="withDetail(messageText, messageDetail)"
      :is-loading="false"
      @update:model-value="closeMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
/* The Help Center tab, lit while you are anywhere under /help. TabsNav styles
   its tabs with `.router-link-exact-active`, which only fires on /help itself,
   so the tab went dark the moment you opened a topic. Matching the tab by its
   href is what is left. Same white the library uses. */
#app.is-help-section :deep(#tabs-nav a[href='/help']) {
  background-color: #fff;
}

/* Off-screen rather than display:none: a hidden element is not focusable.
   `transition: none` matters: the library puts a 0.25s transition on every <a>,
   which would animate this one in across the full 9999px. */
.skip-link {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 1100;
  padding: 0.6rem 1rem;
  background: #fff;
  color: #0f4d90;
  font-weight: 700;
  border: 2px solid #0f4d90;
  border-radius: 0 0 4px 0;
  transition: none;
}

.skip-link:focus {
  left: 0;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  main {
    flex: 1;
    padding-top: 1.5rem;
  }

  /* The header's blue band comes from #nav-wrap, which collapses to zero
     height when there are no nav links, the case on the login screen. Painting
     the header itself keeps the chrome solid on every route. */
  :deep(#app-header) {
    background-color: #0f4d90;
  }

  /* The theme's img { height: auto } beats the height attribute, so size the
     logo here. */
  :deep(#app-logo img) {
    display: block;
    height: 40px;
    width: auto;
  }

  /* Equal-width nav tabs. The library styles each tab `inline-block`, so every
     tab is as wide as its own label and the bar looks ragged. An inline grid of
     equal columns sizes every tab to the widest label instead. */
  :deep(#tabs-nav) {
    display: inline-grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;

    a {
      text-align: center;
    }
  }
}
</style>
